import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthorizationApiProvider } from '../../data-providers/api-providers/authorization-api-provider.service';
import { ISignInRefreshTokenResponseDTO } from '../../view-models/auth.view-model';
import { AuthorisationStateService } from '../authorisation-state.service';
import { LogoutService } from '../logout.service';

@Injectable()
export class HttpAuthInterceptorService implements HttpInterceptor {
  // 401 from this endpoint means the patient is blocked (business rule), not an expired session.
  // It must not trigger the automatic refresh/logout flow below.
  private readonly impersonatePatientAuthenticatePath = '/embedded/patients/authenticate';

  constructor(
    private authorisationStateService: AuthorisationStateService,
    private authorisationApiService: AuthorizationApiProvider,
    private logoutService: LogoutService,
  ) {}

  private cloneRequest(req: HttpRequest<any>, token: string) {
    const headers = req.headers.set('Authorization', `Bearer ${token}`);
    return req.clone({
      headers,
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token: string = this.authorisationStateService.getToken();
    const refreshToken = this.authorisationStateService.getRefreshToken();

    return next.handle(this.cloneRequest(req, token)).pipe(
      catchError((error: HttpErrorResponse) => {
        const { status } = error;
        if (
          status === 401 &&
          !req.url.includes(this.authorisationApiService.refreshTokePath) &&
          !req.url.includes(this.impersonatePatientAuthenticatePath)
        ) {
          if (refreshToken) {
            //try refresh
            return this.handleUnauthorized(req, next, token);
          } else {
            this.logoutService.logout();
          }
        }
        throw error;
      }),
    );
  }

  private handleUnauthorized(req: HttpRequest<any>, next: HttpHandler, expiredToken: string) {
    const refreshToken = this.authorisationStateService.getRefreshToken();
    if (refreshToken) {
      return this.authorisationApiService
        .refreshToken({
          token: expiredToken,
          refresh_token: refreshToken,
        })
        .pipe(
          switchMap((response: ISignInRefreshTokenResponseDTO) => {
            this.authorisationStateService.refreshToken(response.token);
            return next.handle(this.cloneRequest(req, response.token)).pipe(
              catchError((innerError: HttpErrorResponse) => {
                if (innerError.status === 401) {
                  this.logoutService.logout();
                }
                throw innerError;
              }),
            );
          }),
          catchError((err: any) => {
            // Refresh token itself failed (e.g. expired/invalid): the session is already
            // being fully terminated by logout() above (clears state + redirects to login),
            // so there's nothing useful left for the original caller to do with this error.
            // Rethrowing it here used to surface as an unhandled HttpErrorResponse (401) in
            // Sentry even though the logout/redirect worked correctly.
            this.logoutService.logout();
            return EMPTY;
          }),
        );
    }
    return throwError('Refresh token not found');
  }
}
